import React, { useState, useEffect, useMemo, useRef } from "react";
import { ticketRef } from "utilities/ticketNumber";
import { Link } from "react-router-dom";
import { Plus, X, Search, GitBranch, CornerDownRight, ChevronDown, ChevronRight } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { TICKET_STATUS_CATEGORY } from "constants/tickets";
import TicketStatusPill from "components/Tickets/TicketStatusPill";
import { skippedStepsDisplay } from "utilities/conditionalSteps";

/**
 * RelationsPanel (SOUND-497) — workspace right rail.
 * -------------------------------------------------------
 * Typed ticket-to-ticket relations + single-level parent/child. Direction is
 * load-bearing (mirrors the backend): for ticket T,
 *   outgoing depends_on (from=T, to=Y) → "Blocked by Y"
 *   incoming depends_on (from=X, to=T) → "Blocks X"
 * relates_to / duplicates are informational. Only OUTGOING relations are
 * removable here (the backend DELETE requires fromTicketId === this ticket);
 * incoming ones are read-only (managed from the other ticket).
 *
 * Relations carry only IDs, so summaries (#number/title/status) for related
 * tickets are resolved on demand (children arrive as full docs; optimistic
 * rows carry an inline `_summary`). Add/remove/parent are internal-only and
 * optimistic — the page owns the mutation + rollback.
 */
export default function RelationsPanel({
  ticket,
  isInternal = false,
  onAddRelation,
  onRemoveRelation,
  onSetParent,
}) {
  const auth = useAuth();
  const headerCompanyId = useEffectiveCompanyId();
  const selfId = ticket.ticketId;

  const outgoing = ticket.relations?.outgoing || [];
  const incoming = ticket.relations?.incoming || [];
  const children = ticket.children || [];
  const parentId = ticket.parentTicketId || null;
  // SOUND-546 — workflow steps whose `spawnWhen` was not met. Internal only
  // (clients never see sub-tasks at all); null when absent/null/empty.
  const skipped = isInternal ? skippedStepsDisplay(ticket.skippedSteps) : null;
  const [skippedOpen, setSkippedOpen] = useState(false);

  // SOUND-593 — the ADD control lives in the sticky page header (a primary
  // action shouldn't require scrolling the rail). What stays here is the
  // explanation for why it's absent on a closed ticket: the backend refuses new
  // open work under a closed parent. Keys on statusCategory, never a raw status
  // name (standing invariant — a status can be renamed, a category can't).
  const category = ticket.statusCategory || TICKET_STATUS_CATEGORY[ticket.status];
  const isClosed = category === "done" || category === "canceled";
  const closedBlocksChild = isInternal && !parentId && isClosed;

  // The endpoint of a relation that ISN'T this ticket (works both directions).
  const other = (r) => (r.fromTicketId === selfId ? r.toTicketId : r.fromTicketId);
  const isOutgoing = (r) => r.fromTicketId === selfId;

  const pick = (rels, type) => rels.filter((r) => r.type === type);
  const blockedBy = pick(outgoing, "depends_on"); // to = blocker (removable)
  const blocks = pick(incoming, "depends_on"); // from = dependent (read-only)
  const relates = [...pick(outgoing, "relates_to"), ...pick(incoming, "relates_to")];
  const dupOut = pick(outgoing, "duplicates");
  const dupIn = pick(incoming, "duplicates");

  // ---- resolve summaries for referenced ticketIds (children are full docs) ----
  const neededIds = useMemo(() => {
    const ids = new Set();
    [...blockedBy, ...blocks, ...relates, ...dupOut, ...dupIn].forEach((r) => {
      if (!r._summary) {
        const o = r.fromTicketId === selfId ? r.toTicketId : r.fromTicketId;
        if (o) ids.add(o);
      }
    });
    if (parentId && !ticket._parentSummary) ids.add(parentId);
    return Array.from(ids);
  }, [ticket]); // eslint-disable-line react-hooks/exhaustive-deps

  const [summaries, setSummaries] = useState({});
  useEffect(() => {
    let cancelled = false;
    const missing = neededIds.filter((id) => !summaries[id]);
    if (missing.length === 0 || !auth?.user) return;
    Promise.all(
      missing.map((id) =>
        makeApiRequest("get", apiRoutes.getTicketById(id), auth.user, headerCompanyId)
          .then((res) => [id, res.data?.data])
          .catch(() => [id, null])
      )
    ).then((pairs) => {
      if (cancelled) return;
      setSummaries((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, t]) => {
          if (t)
            next[id] = { number: t.number, title: t.title, status: t.status, statusCategory: t.statusCategory };
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [neededIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const summaryOf = (r) => r._summary || summaries[other(r)] || null;

  const [addType, setAddType] = useState(null); // relation type | "parent" | null

  const hasAny =
    blockedBy.length + blocks.length + relates.length + dupOut.length + dupIn.length +
      children.length + (parentId ? 1 : 0) + (skipped ? 1 : 0) >
    0;

  const renderGroup = (title, rows) => {
    if (!rows.length) return null;
    return (
      <div>
        <GroupLabel>{title}</GroupLabel>
        <div className="space-y-1">
          {rows.map((r) => {
            const removable = isInternal && isOutgoing(r) && !r._optimistic;
            return (
              <RelationRow
                key={r.relationId}
                otherId={other(r)}
                summary={summaryOf(r)}
                optimistic={r._optimistic}
                removable={removable}
                onRemove={removable ? () => onRemoveRelation?.(r.relationId) : undefined}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className="surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <GitBranch className="h-3.5 w-3.5" />
          Relations
        </h3>
        {isInternal && !addType && (
          <button type="button" className="btn btn-ghost h-7 px-2 text-xs" onClick={() => setAddType("depends_on")}>
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        )}
      </div>

      {!hasAny && !addType && <p className="text-sm text-muted-foreground">No relations.</p>}

      <div className="space-y-3">
        {renderGroup("Blocked by", blockedBy)}
        {renderGroup("Blocks", blocks)}
        {renderGroup("Relates to", relates)}
        {renderGroup("Duplicates", dupOut)}
        {renderGroup("Duplicate of", dupIn)}

        {/* Parent */}
        {parentId && (
          <div>
            <GroupLabel>Parent</GroupLabel>
            <RelationRow
              otherId={parentId}
              summary={ticket._parentSummary || summaries[parentId]}
              removable={isInternal}
              removeLabel="Clear parent"
              onRemove={() => onSetParent?.(null)}
            />
          </div>
        )}

        {/* Sub-tickets — SOUND-593: the group now renders whenever this ticket
            CAN hold sub-tasks, not only when it already has some, otherwise
            there is nowhere to add the first one. */}
        {(children.length > 0 || closedBlocksChild || skipped) && (
          <div>
            <GroupLabel>
              {children.length > 0 ? `Sub-tickets · ${children.length}` : "Sub-tickets"}
            </GroupLabel>
            {children.length > 0 && (
              <div className="space-y-1">
                {children.map((c) => (
                  <RelationRow
                    key={c.ticketId}
                    otherId={c.ticketId}
                    summary={{ number: c.number, title: c.title, status: c.status, statusCategory: c.statusCategory }}
                    icon={<CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  />
                ))}
              </div>
            )}
            {/* Explain WHY rather than offer a control that can only 400. The
                backend rejects children on a closed parent (reopen first). */}
            {closedBlocksChild && !children.length && (
              <p className="text-xs text-muted-foreground">
                Reopen this ticket to add sub-tasks.
              </p>
            )}
            {skipped && (
              <div className="mt-1.5">
                <button
                  type="button"
                  onClick={() => setSkippedOpen((o) => !o)}
                  aria-expanded={skippedOpen}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {skippedOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  {skipped.summary}
                </button>
                {skippedOpen && (
                  <ul className="mt-1 space-y-0.5 pl-5 text-xs text-muted-foreground">
                    {skipped.titles.map((t, i) => (
                      <li key={i} className="line-through decoration-muted-foreground/50">
                        {t}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add form (internal) */}
      {isInternal && addType && (
        <AddRelationForm
          ticket={ticket}
          authUser={auth.user}
          headerCompanyId={headerCompanyId}
          addType={addType}
          setAddType={setAddType}
          canSetParent={!parentId && children.length === 0}
          onAddRelation={onAddRelation}
          onSetParent={onSetParent}
        />
      )}
    </section>
  );
}

function GroupLabel({ children }) {
  return (
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
      {children}
    </div>
  );
}

function RelationRow({ otherId, summary, optimistic, removable, onRemove, removeLabel = "Remove", icon }) {
  const inner = (
    <>
      {icon}
      {summary ? (
        <>
          {/* An optimistic row has no number yet (the server assigns it), so
              guard rather than render a bare "#". */}
          {summary.number != null && (
            <span className="shrink-0 font-mono text-xs text-muted-foreground">{ticketRef(summary.number)}</span>
          )}
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">{summary.title}</span>
          <TicketStatusPill status={summary.status} statusCategory={summary.statusCategory} />
        </>
      ) : (
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">{otherId}</span>
      )}
    </>
  );

  return (
    <div className={`group flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 ${optimistic ? "opacity-60" : ""}`}>
      {otherId ? (
        <Link to={`/tickets/${otherId}`} className="flex min-w-0 flex-1 items-center gap-2 hover:opacity-80">
          {inner}
        </Link>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">{inner}</div>
      )}
      {removable && !optimistic && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          title={removeLabel}
          className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

const REL_ADD_OPTIONS = [
  { value: "depends_on", label: "Blocked by (depends on)" },
  { value: "relates_to", label: "Relates to" },
  { value: "duplicates", label: "Duplicates" },
  { value: "parent", label: "Set parent" },
];

function AddRelationForm({ ticket, authUser, headerCompanyId, addType, setAddType, canSetParent, onAddRelation, onSetParent }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!authUser) return;
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (query) p.set("search", query);
        p.set("limit", "8");
        const res = await makeApiRequest("get", `${apiRoutes.listTickets}?${p.toString()}`, authUser, headerCompanyId);
        const rows = (res.data?.data || []).filter((t) => t.ticketId !== ticket.ticketId);
        setResults(rows);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const choose = async (t) => {
    const summary = { number: t.number, title: t.title, status: t.status, statusCategory: t.statusCategory };
    setBusy(true);
    try {
      if (addType === "parent") {
        await onSetParent?.(t.ticketId, summary);
      } else {
        await onAddRelation?.({ toTicketId: t.ticketId, type: addType, summary });
      }
      setAddType(null);
      setQuery("");
    } catch {
      /* page toasts */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/20 p-2.5">
      <div className="flex items-center justify-between">
        <select className="select h-8 w-auto text-sm" value={addType} onChange={(e) => setAddType(e.target.value)}>
          {REL_ADD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} disabled={o.value === "parent" && !canSetParent}>
              {o.label}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-ghost h-7 px-2 text-xs" onClick={() => setAddType(null)} disabled={busy}>
          Cancel
        </button>
      </div>
      {addType === "parent" && !canSetParent ? (
        <p className="text-xs text-muted-foreground">
          This ticket already has a parent or sub-tickets (single-level nesting only).
        </p>
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              className="input pl-9"
              placeholder="Search tickets by #number or title…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="max-h-52 overflow-y-auto rounded-md border border-border">
            {loading ? (
              <div className="py-3 text-center text-xs text-muted-foreground">Searching…</div>
            ) : results.length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {query ? "No tickets found." : "Start typing to search."}
              </div>
            ) : (
              <ul className="divide-y divide-border/60">
                {results.map((t) => (
                  <li key={t.ticketId}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => choose(t)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted"
                    >
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">{ticketRef(t.number)}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{t.title}</span>
                      <TicketStatusPill status={t.status} statusCategory={t.statusCategory} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
